import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Save, Key, Mail, Server, Lock, User, AlertCircle, CheckCircle, Info, ChevronDown, ChevronUp, FileText, FileCheck, Shield, Stamp, ScrollText } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import VisualStampBackfillPanel from '../components/VisualStampBackfillPanel';
import { useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const location = useLocation();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPERADMIN';

  // Pobierz typ z URL jeśli istnieje (dla kompatybilności wstecznej)
  const urlType = location.pathname.split('/').pop();
  const validTabs = ['mail', 'ksef', 'cru', 'edor', 'internal-number', 'login-2fa', ...(isSuperAdmin ? ['visual-stamps'] : [])];
  const initialTab = validTabs.includes(urlType) ? urlType : 'mail';
  
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showTestDetails, setShowTestDetails] = useState(false);
  const [ksefStatus, setKsefStatus] = useState(null);
  const [certificateFile, setCertificateFile] = useState(null);
  const [certificatePassword, setCertificatePassword] = useState('');
  const [uploadingCertificate, setUploadingCertificate] = useState(false);
  const [uploadMode, setUploadMode] = useState('PKCS12'); // PKCS12 lub PEM_PAIR
  const [pemCertificateFile, setPemCertificateFile] = useState(null);
  const [pemPrivateKeyFile, setPemPrivateKeyFile] = useState(null);
  
  const tabs = [
    { id: 'mail', label: 'Serwer pocztowy', icon: Mail },
    { id: 'ksef', label: 'KSeF', icon: Key },
    { id: 'cru', label: 'Umowy CRU', icon: ScrollText },
    { id: 'edor', label: 'E-doręczenia', icon: FileCheck },
    { id: 'internal-number', label: 'Numer wewnętrzny', icon: FileText },
    { id: 'login-2fa', label: 'Logowanie 2FA', icon: Shield },
    ...(isSuperAdmin
      ? [{ id: 'visual-stamps', label: 'Stemple wizualne', icon: Stamp }]
      : []),
  ];

  useEffect(() => {
    if (activeTab === 'visual-stamps') {
      setLoading(false);
      return;
    }
    fetchConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setConfig({});
      setTestResult(null);
      setKsefStatus(null);
      
      const endpoint = `/settings/${activeTab}`;
      const response = await api.get(endpoint);
      if (response.data && response.data.config) {
        setConfig(response.data.config);
      }
      
      // Jeśli to KSeF, pobierz również status
      if (activeTab === 'ksef') {
        try {
          const statusResponse = await api.get(`/settings/${activeTab}/status`);
          if (statusResponse.data) {
            setKsefStatus(statusResponse.data);
          }
        } catch (error) {
          // Status jest opcjonalny, ignoruj błędy
          console.debug('Status KSeF nie jest dostępny:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching config:', error);
      if (error.response?.status !== 404) {
        toast.error('Błąd podczas pobierania konfiguracji');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const endpoint = `/settings/${activeTab}`;
      await api.put(endpoint, config);
      toast.success('Konfiguracja zapisana pomyślnie');
      await fetchConfig(); // Odśwież konfigurację po zapisie
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Błąd podczas zapisywania konfiguracji');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field, checked) => {
    setConfig(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleUploadCertificate = async () => {
    setUploadingCertificate(true);
    try {
      const formData = new FormData();
      
      if (uploadMode === 'PKCS12') {
        // Wariant 1: Jeden plik PKCS12
        if (!certificateFile || !certificatePassword) {
          toast.error('Wybierz plik certyfikatu PKCS12 i wprowadź hasło');
          setUploadingCertificate(false);
          return;
        }
        formData.append('file', certificateFile);
        formData.append('password', certificatePassword);
      } else {
        // Wariant 2: Dwa pliki PEM (certyfikat + klucz prywatny)
        if (!pemCertificateFile || !pemPrivateKeyFile || !certificatePassword) {
          toast.error('Wybierz plik certyfikatu, plik klucza prywatnego i wprowadź hasło');
          setUploadingCertificate(false);
          return;
        }
        formData.append('certificateFile', pemCertificateFile);
        formData.append('privateKeyFile', pemPrivateKeyFile);
        formData.append('password', certificatePassword);
      }

      const response = await api.post('/settings/ksef/certificate', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        toast.success('Certyfikat został przesłany pomyślnie');
        setCertificateFile(null);
        setPemCertificateFile(null);
        setPemPrivateKeyFile(null);
        setCertificatePassword('');
        
        // Jeśli backend zwrócił zaktualizowaną konfigurację, użyj jej
        if (response.data.config) {
          setConfig(prev => ({ ...prev, ...response.data.config }));
        } else {
          await fetchConfig(); // W przeciwnym razie odśwież konfigurację
        }
      } else {
        toast.error(response.data.message || 'Błąd podczas przesyłania certyfikatu');
      }
    } catch (error) {
      console.error('Error uploading certificate:', error);
      toast.error(error.response?.data?.message || 'Błąd podczas przesyłania certyfikatu');
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleDeleteCertificate = async () => {
    const confirmMessage = config.certificatePresent 
      ? 'Czy na pewno chcesz usunąć certyfikat? System KSeF nie będzie działał bez certyfikatu.'
      : 'Czy na pewno chcesz wyczyścić certyfikat z bazy danych? To może pomóc w rozwiązaniu problemów z wykrywaniem certyfikatu.';
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setUploadingCertificate(true);
      const response = await api.delete('/settings/ksef/certificate');
      if (response.data.success) {
        toast.success(response.data.message || 'Certyfikat został usunięty');
        
        // Jeśli backend zwrócił zaktualizowaną konfigurację, użyj jej
        if (response.data.config) {
          setConfig(prev => ({ ...prev, ...response.data.config }));
        } else {
          await fetchConfig(); // W przeciwnym razie odśwież konfigurację
        }
      } else {
        toast.error(response.data.message || 'Błąd podczas usuwania certyfikatu');
      }
    } catch (error) {
      console.error('Error deleting certificate:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas usuwania certyfikatu';
      toast.error(errorMessage);
    } finally {
      setUploadingCertificate(false);
    }
  };

  const handleTestConnection = async () => {
    if (activeTab !== 'ksef') {
      // Dla innych typów konfiguracji zachowaj starą logikę
      setTestingConnection(true);
      setTestResult(null);
      
      try {
        await api.put(`/settings/${activeTab}`, config);
        const response = await api.post(`/settings/${activeTab}/test`);
        setTestResult({
          success: response.data.success,
          message: response.data.message
        });
        
        if (response.data.success) {
          toast.success('Test połączenia zakończony sukcesem!');
        } else {
          toast.error('Test połączenia nie powiódł się');
        }
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas testowania połączenia';
        setTestResult({
          success: false,
          message: errorMessage
        });
        toast.error(errorMessage);
      } finally {
        setTestingConnection(false);
      }
      return;
    }
    
    // Dla KSeF - test bez zapisu
    setTestingConnection(true);
    setTestResult(null);
    setShowTestDetails(false);
    
    try {
      // Przygotuj konfigurację do testu
      const testConfig = {
        nip: config.nip,
        environment: config.environment || 'TEST'
      };
      
      // Walidacja - z certyfikatem nie potrzebujemy już tokenu
      
      if (!testConfig.nip || testConfig.nip.length !== 10 || !/^\d{10}$/.test(testConfig.nip)) {
        toast.error('NIP musi składać się z 10 cyfr');
        setTestingConnection(false);
        return;
      }
      
      // Wyślij żądanie testu z konfiguracją w body
      const response = await api.post(`/settings/${activeTab}/test`, testConfig);
      
      setTestResult({
        success: response.data.success,
        message: response.data.message,
        details: response.data.details || null
      });
      
      if (response.data.success) {
        toast.success('Test połączenia zakończony sukcesem!');
        setShowTestDetails(true);
      } else {
        const errorMsg = response.data.message || 'Test połączenia nie powiódł się';
        toast.error(errorMsg);
        setShowTestDetails(true);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Błąd podczas testowania połączenia';
      const errorDetails = error.response?.data?.details || null;
      
      setTestResult({
        success: false,
        message: errorMessage,
        details: errorDetails
      });
      toast.error(errorMessage);
      setShowTestDetails(true);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setTestResult(null);
    setShowTestDetails(false);
  };

  const renderTabContent = () => {
    if (activeTab === 'visual-stamps') {
      return <VisualStampBackfillPanel />;
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      );
    }

    if (activeTab === 'mail') {
      return (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="host" className="block text-sm font-medium text-gray-700 mb-2">
                <Server className="h-4 w-4 inline mr-1" />
                Host SMTP <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="host"
                value={config.host || ''}
                onChange={(e) => handleChange('host', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="smtp.example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="port" className="block text-sm font-medium text-gray-700 mb-2">
                Port <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                id="port"
                value={config.port || ''}
                onChange={(e) => handleChange('port', parseInt(e.target.value) || '')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="587"
                required
                min="1"
                max="65535"
              />
              <p className="mt-1 text-xs text-gray-500">
                Standardowe porty: 587 (TLS), 465 (SSL), 25 (bez szyfrowania)
              </p>
            </div>

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                <User className="h-4 w-4 inline mr-1" />
                Użytkownik (email) <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                id="username"
                value={config.username || ''}
                onChange={(e) => handleChange('username', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                placeholder="uzytkownik@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="h-4 w-4 inline mr-1" />
                Hasło <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={config.password || ''}
                  onChange={(e) => handleChange('password', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Hasło do konta pocztowego"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-sm"
                >
                  {showPassword ? 'Ukryj' : 'Pokaż'}
                </button>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="useTls"
                checked={config.useTls || false}
                onChange={(e) => handleCheckboxChange('useTls', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="useTls" className="ml-2 block text-sm text-gray-700">
                Użyj TLS/STARTTLS
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="useSsl"
                checked={config.useSsl || false}
                onChange={(e) => handleCheckboxChange('useSsl', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="useSsl" className="ml-2 block text-sm text-gray-700">
                Użyj SSL (dla portu 465)
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="requireAuth"
                checked={config.requireAuth !== false}
                onChange={(e) => handleCheckboxChange('requireAuth', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="requireAuth" className="ml-2 block text-sm text-gray-700">
                Wymagaj uwierzytelniania
              </label>
            </div>
          </div>

          <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-sm text-gray-600">
              <strong>Przykładowe konfiguracje:</strong>
            </p>
            <ul className="mt-2 text-xs text-gray-600 space-y-1 list-disc list-inside">
              <li><strong>Gmail:</strong> smtp.gmail.com, port 587, TLS włączony</li>
              <li><strong>Outlook:</strong> smtp-mail.outlook.com, port 587, TLS włączony</li>
              <li><strong>O2:</strong> poczta.o2.pl, port 587, TLS włączony</li>
              <li><strong>WP:</strong> smtp.wp.pl, port 465, SSL włączony</li>
            </ul>
          </div>

          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start">
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <p className={`text-sm ${
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResult.message}
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || saving || !config.host || !config.port || !config.username || !config.password}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testingConnection ? (
                <>
                  <div className="mr-2">
                    <LoadingSpinner size="sm" />
                  </div>
                  Testowanie...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Testuj połączenie
                </>
              )}
            </button>
            <button
              type="submit"
              disabled={saving || testingConnection || !config.host || !config.port || !config.username || !config.password}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="mr-2">
                    <LoadingSpinner size="sm" />
                  </div>
                  Zapisywanie...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Zapisz konfigurację
                </>
              )}
            </button>
          </div>
        </form>
      );
    }

    if (activeTab === 'ksef') {
      return (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-2">Jak uzyskać certyfikat KSeF:</p>
                <ol className="list-decimal list-inside space-y-2 ml-2 text-xs">
                  <li>
                    <strong>Wejdź na stronę KSeF:</strong>{' '}
                    <a 
                      href="https://ksef.mf.gov.pl" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="underline font-semibold text-blue-900 hover:text-blue-700"
                    >
                      https://ksef.mf.gov.pl
                    </a>
                  </li>
                  <li>
                    <strong>Zaloguj się</strong> używając jednej z metod:
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                      <li>Profil Zaufany (ePUAP)</li>
                      <li>Kwalifikowany podpis elektroniczny</li>
                      <li>e-Dowód</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Przejdź do sekcji "Certyfikaty"</strong> lub "Zarządzanie certyfikatami"
                  </li>
                  <li>
                    <strong>Pobierz certyfikat w formacie PKCS12</strong> (.p12 lub .pfx) - to jest standardowy format wydawany przez KSeF
                  </li>
                  <li>
                    <strong>Prześlij certyfikat</strong> używając formularza poniżej
                  </li>
                  <li className="mt-2">
                    <strong>Uwaga:</strong> Jeśli masz tylko plik .crt (certyfikat publiczny), musisz pobrać pełny certyfikat PKCS12 (.p12) z KSeF, który zawiera zarówno certyfikat, jak i klucz prywatny.
                  </li>
                </ol>
                <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-900">
                  <strong>⚠️ Ważne:</strong> Certyfikat jest przechowywany w zaszyfrowanej formie w bazie danych. Hasło do certyfikatu również jest szyfrowane.
                </div>
              </div>
            </div>
          </div>

          {/* Status certyfikatu - informacja o konfiguracji */}
          {/* Konfiguracja KSeF - tylko gdy certyfikat jest w zmiennych środowiskowych (nie w bazie) */}
          {config.nip && !config.certificatePresent && (
            <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">Konfiguracja KSeF</p>
                    <p className="text-xs text-gray-500">
                      Certyfikat jest konfigurowany przez zmienne środowiskowe
                      {config.updatedAt && ` • Ostatnia aktualizacja: ${new Date(config.updatedAt).toLocaleString('pl-PL')}`}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Status ostatniego testu */}
          {ksefStatus && ksefStatus.lastTestAt && (
            <div className={`mb-6 p-4 rounded-lg border ${
              ksefStatus.lastTestSuccess 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center">
                {ksefStatus.lastTestSuccess ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0" />
                )}
                <div>
                  <p className={`text-sm font-medium ${
                    ksefStatus.lastTestSuccess ? 'text-green-900' : 'text-red-900'
                  }`}>
                    Ostatni test: {ksefStatus.lastTestSuccess ? 'Sukces' : 'Błąd'}
                  </p>
                  <p className={`text-xs ${
                    ksefStatus.lastTestSuccess ? 'text-green-700' : 'text-red-700'
                  }`}>
                    {new Date(ksefStatus.lastTestAt).toLocaleString('pl-PL')}
                    {ksefStatus.lastMessage && ` • ${ksefStatus.lastMessage}`}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Status certyfikatu */}
          {config.certificatePresent && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-green-900">Certyfikat załadowany</p>
                    <p className="text-xs text-green-700">
                      {config.certificateFileName && `Plik: ${config.certificateFileName}`}
                      {config.certificateSize && ` (${(config.certificateSize / 1024).toFixed(2)} KB)`}
                      {config.uploadMode && ` • Tryb: ${config.uploadMode === 'PKCS12' ? 'PKCS12' : 'PEM'}`}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDeleteCertificate}
                  disabled={uploadingCertificate}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {uploadingCertificate ? 'Usuwanie...' : 'Usuń certyfikat'}
                </button>
              </div>
            </div>
          )}
          
          {/* Przycisk usuwania również gdy certyfikat nie jest wykryty, ale może istnieć w bazie */}
          {!config.certificatePresent && config.nip && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
                  <div>
                    <p className="text-sm font-medium text-yellow-900">Certyfikat nie wykryty</p>
                    <p className="text-xs text-yellow-700">
                      Jeśli certyfikat został wgryty, ale nie jest widoczny, możesz spróbować go usunąć i wgrać ponownie
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDeleteCertificate}
                  disabled={uploadingCertificate}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  {uploadingCertificate ? 'Czyszczenie...' : 'Wyczyść certyfikat'}
                </button>
              </div>
            </div>
          )}

          {/* Formularz uploadu certyfikatu */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="text-sm font-medium text-blue-900 mb-3">
              {config.certificatePresent ? 'Zastąp certyfikat' : 'Prześlij certyfikat KSeF'}
            </h3>
            
            {/* Wybór trybu uploadu */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tryb przesyłania:
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="uploadMode"
                    value="PKCS12"
                    checked={uploadMode === 'PKCS12'}
                    onChange={(e) => setUploadMode(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Jeden plik PKCS12 (.p12/.pfx)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="uploadMode"
                    value="PEM_PAIR"
                    checked={uploadMode === 'PEM_PAIR'}
                    onChange={(e) => setUploadMode(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">Dwa pliki PEM (cert + klucz)</span>
                </label>
              </div>
            </div>
            
            <div className="space-y-4">
              {uploadMode === 'PKCS12' ? (
                <>
                  {/* Wariant 1: Jeden plik PKCS12 */}
                  <div>
                    <label htmlFor="certificateFile" className="block text-sm font-medium text-gray-700 mb-2">
                      Plik certyfikatu PKCS12 (.p12 lub .pfx) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      id="certificateFile"
                      accept=".p12,.pfx"
                      onChange={(e) => setCertificateFile(e.target.files[0])}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      required={!config.certificatePresent}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Certyfikat musi być w formacie PKCS12 (.p12 lub .pfx) zawierający zarówno certyfikat, jak i klucz prywatny
                    </p>
                  </div>
                </>
              ) : (
                <>
                  {/* Wariant 2: Dwa pliki PEM */}
                  <div>
                    <label htmlFor="pemCertificateFile" className="block text-sm font-medium text-gray-700 mb-2">
                      Plik certyfikatu (.crt, .cer, .pem) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      id="pemCertificateFile"
                      accept=".crt,.cer,.pem"
                      onChange={(e) => setPemCertificateFile(e.target.files[0])}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      required={!config.certificatePresent}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Certyfikat publiczny w formacie PEM
                    </p>
                  </div>
                  <div>
                    <label htmlFor="pemPrivateKeyFile" className="block text-sm font-medium text-gray-700 mb-2">
                      Plik klucza prywatnego (.key, .pem) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      id="pemPrivateKeyFile"
                      accept=".key,.pem"
                      onChange={(e) => setPemPrivateKeyFile(e.target.files[0])}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                      required={!config.certificatePresent}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Klucz prywatny w formacie PEM (PKCS8)
                    </p>
                  </div>
                </>
              )}
              
              <div>
                <label htmlFor="certificatePassword" className="block text-sm font-medium text-gray-700 mb-2">
                  {uploadMode === 'PKCS12' ? 'Hasło do certyfikatu PKCS12' : 'Hasło do ochrony certyfikatu PKCS12'} <span className="text-red-500">*</span>
                </label>
                <input
                  type={showPassword ? "text" : "password"}
                  id="certificatePassword"
                  value={certificatePassword || ''}
                  onChange={(e) => setCertificatePassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder={uploadMode === 'PKCS12' ? "Wprowadź hasło do certyfikatu PKCS12" : "Wprowadź hasło do ochrony wygenerowanego certyfikatu PKCS12"}
                  required={!config.certificatePresent}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="mt-1 text-xs text-gray-600 hover:text-gray-800"
                >
                  {showPassword ? 'Ukryj' : 'Pokaż'} hasło
                </button>
                {uploadMode === 'PEM_PAIR' && (
                  <p className="mt-1 text-xs text-blue-600">
                    ℹ️ Pliki PEM zostaną automatycznie skonwertowane do PKCS12. Hasło będzie używane do ochrony wygenerowanego certyfikatu.
                  </p>
                )}
              </div>
              
              <button
                type="button"
                onClick={handleUploadCertificate}
                disabled={uploadingCertificate || 
                  (uploadMode === 'PKCS12' && (!certificateFile || !certificatePassword)) ||
                  (uploadMode === 'PEM_PAIR' && (!pemCertificateFile || !pemPrivateKeyFile || !certificatePassword))}
                className="w-full flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadingCertificate ? (
                  <>
                    <div className="mr-2">
                      <LoadingSpinner size="sm" />
                    </div>
                    Przesyłanie...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    {config.certificatePresent ? 'Zastąp certyfikat' : 'Prześlij certyfikat'}
                  </>
                )}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="nip" className="block text-sm font-medium text-gray-700 mb-2">
              NIP <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="nip"
              value={config.nip || ''}
              onChange={(e) => handleChange('nip', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="Wprowadź NIP (10 cyfr)"
              required
              pattern="[0-9]{10}"
              maxLength={10}
            />
            <p className="mt-1 text-xs text-gray-500">
              NIP firmy, w imieniu której będą wykonywane operacje w KSeF
            </p>
          </div>

          <div>
            <label htmlFor="environment" className="block text-sm font-medium text-gray-700 mb-2">
              Środowisko <span className="text-red-500">*</span>
            </label>
            <select
              id="environment"
              value={config.environment || 'TEST'}
              onChange={(e) => handleChange('environment', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="TEST">Testowe (TEST)</option>
              <option value="DEMO">Demo (DEMO)</option>
              <option value="PROD">Produkcyjne (PROD)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Wybierz środowisko do komunikacji z KSeF
            </p>
          </div>

          {testResult && (
            <div className={`p-4 rounded-lg border ${
              testResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-start">
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${
                    testResult.success ? 'text-green-800' : 'text-red-800'
                  }`}>
                    {testResult.message}
                  </p>
                  
                  {testResult.details && (
                    <button
                      type="button"
                      onClick={() => setShowTestDetails(!showTestDetails)}
                      className="mt-2 flex items-center text-xs text-gray-600 hover:text-gray-800"
                    >
                      {showTestDetails ? (
                        <>
                          <ChevronUp className="h-4 w-4 mr-1" />
                          Ukryj szczegóły
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-4 w-4 mr-1" />
                          Pokaż szczegóły
                        </>
                      )}
                    </button>
                  )}
                  
                  {testResult.details && showTestDetails && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      <div className="text-xs space-y-1">
                        {testResult.details.environment && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Środowisko:</span>
                            <span className="text-gray-600">{testResult.details.environment}</span>
                          </div>
                        )}
                        {testResult.details.baseUrl && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">URL:</span>
                            <span className="text-gray-600 break-all">{testResult.details.baseUrl}</span>
                          </div>
                        )}
                        {testResult.details.referenceNumber && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Reference:</span>
                            <span className="text-gray-600 font-mono text-xs">{testResult.details.referenceNumber}</span>
                          </div>
                        )}
                        {testResult.details.authStatusCode !== undefined && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Status:</span>
                            <span className={`font-medium ${
                              testResult.details.authStatusCode === 200 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {testResult.details.authStatusCode}
                            </span>
                          </div>
                        )}
                        {testResult.details.latencyMs !== undefined && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Czas odpowiedzi:</span>
                            <span className="text-gray-600">{testResult.details.latencyMs} ms</span>
                          </div>
                        )}
                        {testResult.details.stepLatencies && (
                          <div className="mt-2 pt-2 border-t border-gray-200">
                            <p className="font-medium text-gray-700 mb-1">Czasy poszczególnych kroków:</p>
                            {Object.entries(testResult.details.stepLatencies).map(([step, latency]) => (
                              <div key={step} className="flex text-xs">
                                <span className="font-medium text-gray-600 w-24 capitalize">{step}:</span>
                                <span className="text-gray-500">{latency} ms</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {testResult.details.step && !testResult.success && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Krok błędu:</span>
                            <span className="text-red-600">{testResult.details.step}</span>
                          </div>
                        )}
                        {testResult.details.httpStatus && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">HTTP Status:</span>
                            <span className="text-red-600">{testResult.details.httpStatus}</span>
                          </div>
                        )}
                        {testResult.details.ksefErrorCode && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Kod błędu KSeF:</span>
                            <span className="text-red-600 font-mono">{testResult.details.ksefErrorCode}</span>
                          </div>
                        )}
                        {testResult.details.ksefStatusCode !== undefined && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Kod statusu KSeF:</span>
                            <span className="text-red-600 font-medium">
                              {testResult.details.ksefStatusCode}
                              {testResult.details.ksefStatusCode === 450 && (
                                <span className="ml-2 text-xs">(błędny certyfikat)</span>
                              )}
                            </span>
                          </div>
                        )}
                        {testResult.details.ksefErrorDescription && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Opis błędu KSeF:</span>
                            <span className="text-red-600 text-sm">{testResult.details.ksefErrorDescription}</span>
                          </div>
                        )}
                        {testResult.details.ksefStatusDetails && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Szczegóły statusu:</span>
                            <span className="text-red-600 text-sm font-mono">{testResult.details.ksefStatusDetails}</span>
                          </div>
                        )}
                        {testResult.details.nip && (
                          <div className="flex">
                            <span className="font-medium text-gray-700 w-32">Testowany NIP:</span>
                            <span className="text-gray-600">{testResult.details.nip}</span>
                          </div>
                        )}
                        {testResult.details.note && (
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                            <strong>Uwaga:</strong> {testResult.details.note}
                          </div>
                        )}
                        {testResult.details.hints && testResult.details.hints.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="font-medium text-gray-700 mb-2">💡 Wskazówki:</p>
                            <ul className="list-disc list-inside space-y-1 text-xs text-gray-600">
                              {testResult.details.hints.map((hint, index) => (
                                <li key={index}>{hint}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleTestConnection}
              disabled={testingConnection || saving || !config.nip || config.nip.length !== 10 || !config.environment}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {testingConnection ? (
                <>
                  <div className="mr-2">
                    <LoadingSpinner size="sm" />
                  </div>
                  Testowanie...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Testuj połączenie
                </>
              )}
            </button>
            <button
              type="submit"
              disabled={saving || testingConnection || !config.nip || config.nip.length !== 10 || !config.environment}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="mr-2">
                    <LoadingSpinner size="sm" />
                  </div>
                  Zapisywanie...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Zapisz konfigurację
                </>
              )}
            </button>
          </div>
        </form>
      );
    }

    if (activeTab === 'cru') {
      const cruEnv = config.environment || 'TEST';
      const defaultBaseUrl = cruEnv === 'PROD'
        ? 'https://jsfp.rejestrumow.gov.pl/v1/api/publish'
        : 'https://jsfp-cru-test.mf.gov.pl/v1/api/publish';
      return (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="mb-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Integracja z Centralnym Rejestrem Umów JSFP (CRU)</p>
                <p className="text-xs">
                  Tutaj ustawiasz wspólną dla jednostki konfigurację CRU: środowisko, adres bazowy API oraz REGON jednostki.
                </p>
              </div>
            </div>
          </div>

          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start">
            <Key className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-amber-900">
              <p className="font-semibold mb-1">Klucz API jest indywidualny dla każdego użytkownika</p>
              <p className="text-xs">
                Klucz API (X-API-KEY) nie jest już wspólny. Każdy użytkownik z dostępem do modułu wprowadza własny klucz
                wygenerowany na swoim koncie w aplikacji CRU — bezpośrednio w module{' '}
                <strong>Umowy CRU</strong>.
              </p>
            </div>
          </div>

          <div>
            <label htmlFor="cruEnvironment" className="block text-sm font-medium text-gray-700 mb-2">
              Środowisko <span className="text-red-500">*</span>
            </label>
            <select
              id="cruEnvironment"
              value={cruEnv}
              onChange={(e) => handleChange('environment', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="TEST">Testowe (TEST)</option>
              <option value="PROD">Produkcyjne (PROD)</option>
            </select>
          </div>

          <div>
            <label htmlFor="cruRegon" className="block text-sm font-medium text-gray-700 mb-2">
              REGON jednostki
            </label>
            <input
              type="text"
              id="cruRegon"
              value={config.regon || ''}
              onChange={(e) => handleChange('regon', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="np. 123456789"
            />
            <p className="mt-1 text-xs text-gray-500">
              REGON jednostki przypisanej do klucza API – pierwsza strona umowy musi wskazywać tę jednostkę.
            </p>
          </div>

          <div>
            <label htmlFor="cruBaseUrl" className="block text-sm font-medium text-gray-700 mb-2">
              Adres bazowy API (opcjonalnie)
            </label>
            <input
              type="text"
              id="cruBaseUrl"
              value={config.baseUrl || ''}
              onChange={(e) => handleChange('baseUrl', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={defaultBaseUrl}
            />
            <p className="mt-1 text-xs text-gray-500">
              Pozostaw puste, aby użyć domyślnego adresu dla wybranego środowiska: <span className="font-mono">{defaultBaseUrl}</span>
            </p>
          </div>

          <div className="flex justify-end pt-4 border-t">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center px-6 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <div className="mr-2">
                    <LoadingSpinner size="sm" />
                  </div>
                  Zapisywanie...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Zapisz konfigurację
                </>
              )}
            </button>
          </div>
        </form>
      );
    }

    if (activeTab === 'internal-number') {
      return (
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Wzorzec numeru wewnętrznego
              </label>
              <input
                type="text"
                value={config.pattern || '{NUMBER}/{PREFIX}/{YEAR}'}
                onChange={(e) => setConfig({ ...config, pattern: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="{NUMBER}/{PREFIX}/{YEAR}"
              />
              <p className="mt-2 text-sm text-gray-500">
                Dostępne placeholdery: {'{NUMBER}'} - numer sekwencyjny, {'{PREFIX}'} - prefiks, {'{YEAR}'} - rok
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Przykład: {'{NUMBER}'}/{'{PREFIX}'}/{'{YEAR}'} → 1/LAPY/2026
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Prefiks
              </label>
              <input
                type="text"
                value={config.prefix || 'LAPY'}
                onChange={(e) => setConfig({ ...config, prefix: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="LAPY"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numer początkowy
              </label>
              <input
                type="number"
                value={config.startNumber || 1}
                onChange={(e) => setConfig({ ...config, startNumber: parseInt(e.target.value) || 1 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                min="1"
              />
              <p className="mt-2 text-sm text-gray-500">
                Numer od którego zacznie się generowanie numerów wewnętrznych dla każdego roku
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Zapisz konfigurację
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      );
    }

    if (activeTab === 'edor') {
      return (
        <div className="text-center py-12">
          <FileCheck className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Konfiguracja e-doręczeń</h3>
          <p className="text-gray-600">Funkcja w przygotowaniu</p>
        </div>
      );
    }

    if (activeTab === 'login-2fa') {
      return (
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-blue-900 mb-1">
                    Podwójna weryfikacja przy logowaniu
                  </h3>
                  <p className="text-sm text-blue-700">
                    Po włączeniu tej opcji, wszyscy użytkownicy (oprócz admin@crm.local) będą musieli wprowadzić 6-cyfrowy kod weryfikacyjny wysłany na email podczas logowania.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="login2faEnabled" className="block text-sm font-medium text-gray-900 mb-1">
                    Włącz podwójną weryfikację (2FA)
                  </label>
                  <p className="text-sm text-gray-500">
                    Gdy włączone, użytkownicy otrzymają kod weryfikacyjny na email przy każdym logowaniu
                  </p>
                </div>
                <div className="ml-4">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="login2faEnabled"
                      checked={config.enabled || false}
                      onChange={(e) => handleCheckboxChange('enabled', e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {config.enabled && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-yellow-900 mb-1">
                      Ważne informacje
                    </h4>
                    <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                      <li>Kod weryfikacyjny jest ważny przez 10 minut</li>
                      <li>Użytkownik admin@crm.local zawsze loguje się bez 2FA</li>
                      <li>Upewnij się, że serwer pocztowy jest poprawnie skonfigurowany</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Zapisywanie...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Zapisz ustawienia
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      );
    }

    return null;
  };

  return (
    <div className="card">
      <div className="card-header">
        <h2 className="text-xl font-bold text-gray-900">Ustawienia</h2>
      </div>
      
      {/* Zakładki */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-1 px-4 sm:px-5 md:px-6" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-150
                  ${isActive
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                <Icon className={`h-4 w-4 mr-2 ${isActive ? 'text-primary-600' : 'text-gray-400'}`} />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Zawartość zakładki */}
      <div className="card-body">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default Settings;
